/**
 * AccountCard Component
 * Displays a single account with balance and metadata
 */
import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { formatCurrency, formatRelativeTime, formatAccountMask } from '@/utils/format';
import AccountTypeIcon from './AccountTypeIcon';
import Tooltip from '@/components/ui/Tooltip';
import type { AccountWithConnection } from '@/services/accounts';

export interface AccountCardProps {
  account: AccountWithConnection;
  showInstitution?: boolean;
  className?: string;
  /** Callback when close button is clicked (only shown for manual accounts) */
  onCloseClick?: (account: AccountWithConnection) => void;
  /** Callback when edit button is clicked (shown for all accounts) - WP-FLOW-7 */
  onEditClick?: (account: AccountWithConnection) => void;
  /** Callback when balance history button is clicked (shown for all accounts) - WP-FLOW-10 */
  onBalanceHistoryClick?: (account: AccountWithConnection) => void;
  /** Callback when holdings button is clicked (shown for all accounts) - WP-MANUAL-HOLD-2 */
  onHoldingsClick?: (account: AccountWithConnection) => void;
  /** Callback when merge button is clicked on any account */
  onMergeClick?: (account: AccountWithConnection) => void;
  /** Callback when delete button is clicked (shown for all accounts) - WP-ACCT-2 */
  onDeleteClick?: (account: AccountWithConnection) => void;
  /** Callback when hide/unhide button is clicked (shown for all accounts) - WP-BUG-62 */
  onToggleVisibilityClick?: (account: AccountWithConnection) => void;
}

export default function AccountCard({
  account,
  showInstitution = true,
  className,
  onCloseClick,
  onEditClick,
  onBalanceHistoryClick,
  onHoldingsClick,
  onMergeClick,
  onDeleteClick,
  onToggleVisibilityClick,
}: AccountCardProps) {
  // Backend sends balances with correct sign: positive = asset/credit, negative = debt
  // Display the balance directly without transformation (matches ConnectionCard behavior)
  const displayBalance = account.balance;

  const isNegative = displayBalance < 0;
  const hasConnectionError = account.connection?.status === 'error' || account.connection?.status === 'pending_reauth';

  // Show close button for manual accounts (no connection_id)
  const isManualAccount = !account.connectionId;
  const showCloseButton = isManualAccount && onCloseClick;

  // WP-ACCT-2: Orphaned account detection (for visual indicator)
  // An account is orphaned when its connection is disconnected or deleted
  const isDisconnected = account.connection?.status === 'disconnected';
  const isOrphaned = account.connectionId && isDisconnected;
  // Delete button shown for ALL accounts - user can delete any account they want
  const showDeleteButton = !!onDeleteClick;

  const handleCloseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCloseClick?.(account);
  };

  // WP-FLOW-7: Edit button handler
  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEditClick?.(account);
  };

  // WP-FLOW-10: Balance history button handler
  const handleBalanceHistoryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onBalanceHistoryClick?.(account);
  };

  // WP-MANUAL-HOLD-2: Holdings button handler
  const handleHoldingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onHoldingsClick?.(account);
  };

  // Merge button handler
  const handleMergeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMergeClick?.(account);
  };

  // WP-ACCT-2: Delete button handler for all accounts
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDeleteClick?.(account);
  };

  // WP-BUG-62: Hide/unhide handler. Without this control a hidden account
  // could never be recovered from the UI.
  const handleToggleVisibilityClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleVisibilityClick?.(account);
  };

  return (
    <Link
      to={`/transactions?accountId=${account.id}`}
      className={cn(
        'block p-4 rounded-lg border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all group',
        account.isHidden && 'opacity-60',
        className
      )}
      aria-label={`${account.name} account with balance ${formatCurrency(displayBalance, account.currency)}`}
    >
      <div className="flex items-start gap-3">
        <AccountTypeIcon type={account.type} size="md" />

        <div className="flex-1 min-w-0">
          {/* Account name and mask */}
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900 truncate">{account.name}</h3>
            {account.mask && (
              <span className="text-sm text-gray-500 flex-shrink-0">
                {formatAccountMask(account.mask)}
              </span>
            )}
          </div>

          {/* Institution name or Manual badge */}
          {showInstitution && (
            <p className="text-sm text-gray-500 truncate">
              {account.sourceType === 'manual' ? (
                <span className="inline-flex items-center gap-1">
                  <PencilIcon className="h-3.5 w-3.5" />
                  Manual
                </span>
              ) : account.connection?.institutionName ? (
                account.connection.institutionName
              ) : null}
            </p>
          )}

          {/* Last sync status */}
          {account.connection?.lastSyncAt && (
            <p className="text-xs text-gray-400 mt-1">
              Updated {formatRelativeTime(account.connection.lastSyncAt)}
            </p>
          )}

          {/* Connection error indicator */}
          {hasConnectionError && (
            <div className="flex items-center gap-1 mt-1">
              <WarningIcon className="h-4 w-4 text-yellow-500" />
              <span className="text-xs text-yellow-600">
                {account.connection?.status === 'pending_reauth'
                  ? 'Reconnection required'
                  : 'Sync error'}
              </span>
            </div>
          )}

          {/* WP-BUG-62: Hidden indicator - opacity alone was not discoverable,
              so a hidden account read as missing rather than hidden. */}
          {account.isHidden && (
            <div className="flex items-center gap-1 mt-1">
              <EyeSlashIcon className="h-4 w-4 text-gray-400" />
              <span className="text-xs text-gray-500">
                Hidden from lists and totals
              </span>
            </div>
          )}

          {/* WP-ACCT-2: Orphaned account indicator */}
          {isOrphaned && (
            <div className="flex items-center gap-1 mt-1">
              <LinkSlashIcon className="h-4 w-4 text-gray-400" />
              <span className="text-xs text-gray-500">
                Connection removed
              </span>
            </div>
          )}
        </div>

        {/* Balance and actions */}
        <div className="flex items-start gap-2">
          <div className="text-right flex-shrink-0">
            <p
              className={cn(
                'font-semibold tabular-nums',
                isNegative ? 'text-red-600' : 'text-gray-900'
              )}
            >
              {formatCurrency(displayBalance, account.currency)}
            </p>
            {account.availableBalance !== undefined && account.availableBalance !== account.balance && (
              <p className="text-xs text-gray-500">
                {formatCurrency(account.availableBalance, account.currency)} available
              </p>
            )}
          </div>

          {/* WP-BUG-62: Hide/unhide button for all accounts */}
          {onToggleVisibilityClick && (
            <Tooltip
              content={account.isHidden ? 'Unhide account' : 'Hide account'}
              position="top"
            >
              <button
                type="button"
                onClick={handleToggleVisibilityClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                aria-label={
                  account.isHidden
                    ? `Unhide account ${account.name}`
                    : `Hide account ${account.name}`
                }
                aria-pressed={account.isHidden}
              >
                {account.isHidden ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </Tooltip>
          )}

          {/* WP-MANUAL-HOLD-2: Holdings button for all accounts */}
          {onHoldingsClick && (
            <Tooltip content="View holdings" position="top">
              <button
                type="button"
                onClick={handleHoldingsClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                aria-label={`View holdings for ${account.name}`}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* WP-FLOW-10: Balance history button for all accounts */}
          {onBalanceHistoryClick && (
            <Tooltip content="View balance history" position="top">
              <button
                type="button"
                onClick={handleBalanceHistoryClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                aria-label={`View balance history for ${account.name}`}
              >
                <ClockIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* WP-FLOW-7: Edit button for all accounts */}
          {onEditClick && (
            <Tooltip content="Edit account name" position="top">
              <button
                type="button"
                onClick={handleEditClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                aria-label={`Edit account ${account.name}`}
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* Merge button for all accounts */}
          {onMergeClick && (
            <Tooltip content="Merge account" position="top">
              <button
                type="button"
                onClick={handleMergeClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                aria-label={`Merge account ${account.name}`}
              >
                <MergeSmallIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* Close button for manual accounts */}
          {showCloseButton && (
            <Tooltip content="Close account" position="top">
              <button
                type="button"
                onClick={handleCloseClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                aria-label={`Close account ${account.name}`}
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* WP-ACCT-2: Delete button for all accounts */}
          {showDeleteButton && (
            <Tooltip content="Delete account" position="top">
              <button
                type="button"
                onClick={handleDeleteClick}
                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                aria-label={`Delete account ${account.name}`}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </Link>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

// WP-FLOW-7: Edit button icon (pencil with square)
function PencilSquareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

// WP-FLOW-10: Balance history button icon (clock)
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

// WP-MANUAL-HOLD-2: Holdings button icon (grid)
function Squares2X2Icon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
      />
    </svg>
  );
}

// Merge button icon (arrows pointing inward)
function MergeSmallIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
      />
    </svg>
  );
}

// WP-ACCT-2: Trash icon for delete button
function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

// WP-ACCT-2: Link slash icon for orphaned account indicator
function LinkSlashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.181 8.68a4.503 4.503 0 011.903 6.405m-9.768-2.782L3.56 14.06a4.5 4.5 0 006.364 6.365l.457-.456m7.364-7.364l1.591-1.591a4.5 4.5 0 00-6.364-6.364l-.457.456M2.25 21.75l19.5-19.5"
      />
    </svg>
  );
}

/**
 * Skeleton loading state for AccountCard
 */
export function AccountCardSkeleton() {
  return (
    <div className="block p-4 rounded-lg border border-gray-200 bg-white animate-pulse">
      <div className="flex items-start gap-3">
        {/* Icon skeleton */}
        <div className="h-10 w-10 rounded-lg bg-gray-200" />

        <div className="flex-1 min-w-0">
          {/* Name skeleton */}
          <div className="h-5 w-32 bg-gray-200 rounded" />
          {/* Institution skeleton */}
          <div className="h-4 w-24 bg-gray-200 rounded mt-1" />
          {/* Sync time skeleton */}
          <div className="h-3 w-20 bg-gray-200 rounded mt-2" />
        </div>

        {/* Balance skeleton */}
        <div className="text-right">
          <div className="h-5 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
}

// WP-BUG-62: Visibility toggle icons (heroicons outline eye / eye-slash)
function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function EyeSlashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88"
      />
    </svg>
  );
}
